import type { PricingSettings, Product } from '@/types'
import type { ModifierOptionPrice } from '@/lib/lineModifiers'
import { resolveLineUnitPrice } from '@/lib/lineModifiers'
import { computeCartTotals } from '@/lib/counterPricing'
import type { OfflineOrder } from './offlineStore'

/**
 * Price a held offline order from the cached menu (F-FE-N77).
 *
 * WHY THIS EXISTS
 *
 * A queue entry stores ids and quantities only — never a price. That is
 * deliberate (see `OfflineLine`): a figure cached at capture time is a stale
 * number waiting to be resurrected. So anything that needs the order's value —
 * showing it on a tray card, charging for it — has to re-derive it, exactly the
 * way `pricedCart` re-derives the live cart.
 *
 * The first cut of offline payment dodged this by grabbing the totals off the
 * on-screen cart the instant the order was fired, which forced payment to be a
 * modal that ambushed the cashier before they could do anything else. Pricing
 * the entry directly is what lets payment become an action you take on a card,
 * whenever you like.
 *
 * Same computation as the counter's own preview — `resolveLineUnitPrice` folds
 * modifier deltas into the unit price, then `computeCartTotals` applies service
 * charge and tax for the tender. It is still not the pricing authority: the
 * server reprices on replay and its figure wins (invariant #11). This is what
 * the customer is asked for at the counter, not what the order settles at.
 */
export interface OfflineOrderPricing {
  lines: Array<{
    name: string
    quantity: number
    unitPrice: number
    lineTotal: number
    /** True when a line's product is missing from the cached menu. */
    unknown: boolean
  }>
  subtotal: number
  tax: number
  serviceCharge: number
  deliveryFee: number
  total: number
  /**
   * Lines whose product could not be found in the cached menu.
   *
   * Not a theoretical case: a product deleted or made inactive after this
   * terminal last synced its menu resolves to nothing. Priced at zero and
   * counted here so the UI can refuse to take payment on a total it knows is
   * wrong, rather than quietly undercharging.
   */
  unknownLines: number
}

export function priceOfflineOrder(
  entry: OfflineOrder,
  deps: {
    products: Product[]
    /** F-MOD-N13: per product — a shared option can be priced per item. */
    optionByIdFor: (productId: string) => ReadonlyMap<string, { name: string }>
    priceIndexFor: (productId: string) => ReadonlyMap<string, ModifierOptionPrice>
    pricing: PricingSettings
    intent: 'cash' | 'card' | 'online'
    includeServiceCharge: boolean
    deliveryFee: number
    /** F-MM-N23: from the order-type config at capture time (no server stamp exists yet). */
    taxTreatment?: 'standard' | 'marketplace_settled'
    /**
     * F-DEAL-N2: the product's base price for this order's type at the moment
     * it was captured (happy hour / channel price). The server prices the
     * replay the same way. Omitted = the list price.
     */
    basePriceFor?: (product: Product, entry: OfflineOrder) => number
  },
): OfflineOrderPricing {
  const byId = new Map(deps.products.map((p) => [p.id, p]))

  const lines = entry.lines.map((l) => {
    const product = byId.get(l.productId)
    const selections = (l.selections ?? []).map((s) => ({
      option_id: s.option_id,
      quantity: s.quantity,
    }))
    const unitPrice = product
      ? resolveLineUnitPrice(deps.basePriceFor ? deps.basePriceFor(product, entry) : product.price, selections, deps.priceIndexFor(product.id))
      : 0

    // The same label the kitchen slip printed: product plus chosen options.
    const labels = selections
      .map((s) => {
        const o = deps.optionByIdFor(l.productId).get(s.option_id)
        if (!o) return ''
        return s.quantity > 1 ? `${o.name} x${s.quantity}` : o.name
      })
      .filter(Boolean)

    const base = product?.name ?? 'Unknown item'
    return {
      name: labels.length > 0 ? `${base} (${labels.join(', ')})` : base,
      quantity: l.quantity,
      unitPrice,
      lineTotal: unitPrice * l.quantity,
      unknown: product == null,
    }
  })

  const subtotal = lines.reduce((t, l) => t + l.lineTotal, 0)
  const totals = computeCartTotals(subtotal, 0, deps.intent, deps.pricing, {
    includeServiceCharge: deps.includeServiceCharge,
    deliveryFee: deps.deliveryFee,
    serviceChargeRateOverride: null,
    serviceChargeAmountOverride: null,
    taxTreatment: deps.taxTreatment,
  })

  return {
    lines,
    subtotal,
    tax: totals.tax,
    serviceCharge: totals.service,
    deliveryFee: totals.delivery,
    total: totals.total,
    unknownLines: lines.filter((l) => l.unknown).length,
  }
}
